/**
 * AI Chat Component
 *
 * Conversational AI interface for asking questions about restaurant insights.
 */

import React, { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Icon from '@/src/@core/components/icon'
import { aiInsightsApi } from '../services/aiInsightsApi'
import type { ChatMessage } from '../types/aiInsightsTypes'
import { RestaurantSelector } from './RestaurantSelector'
import { SuggestedQuestions } from './SuggestedQuestions'
import { updateConversation, getConversation } from '../utils/chatHistory'
import { ChatMessageContent } from './ChatMessageContent'

interface AIChatProps {
  onNewConversation?: () => void
  onQuestionClick?: (question: string) => void
  conversationId?: string // Load existing conversation
  onConversationIdChange?: (conversationId: string | undefined) => void
}

export const AIChat: React.FC<AIChatProps> = ({
  onQuestionClick,
  conversationId: propConversationId,
  onConversationIdChange
}) => {
  const theme = useTheme()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>(propConversationId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [useAllRestaurants, setUseAllRestaurants] = useState(true)
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<number[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Load conversation when conversationId prop changes
  useEffect(() => {
    if (propConversationId) {
      const conversation = getConversation(propConversationId)
      if (conversation) {
        setMessages(conversation.messages)
        setConversationId(propConversationId)
      }
    } else {
      // New conversation
      setMessages([])
      setConversationId(undefined)
    }
  }, [propConversationId])

  // Handle question from proactive insights
  useEffect(() => {
    if (onQuestionClick) {
      const handleQuestion = (question: string) => {
        setInput(question)

        // Auto-focus input after setting question
        setTimeout(() => {
          inputRef.current?.focus()
        }, 100)
      }

      // Store handler for parent to call
      ;(window as any).__aiChatHandleQuestion = handleQuestion

      return () => {
        delete (window as any).__aiChatHandleQuestion
      }
    }
  }, [onQuestionClick])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || loading) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setLoading(true)
    setError(null)

    try {
      const response = await aiInsightsApi.chat({
        message: userMessage.content,
        conversationId,
        storeIds: useAllRestaurants ? undefined : selectedRestaurantIds.length > 0 ? selectedRestaurantIds : undefined
      })

      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date()
      }

      const updatedMessages = [...messages, userMessage, aiMessage]
      setMessages(updatedMessages)

      const newConversationId = response.conversationId
      setConversationId(newConversationId)

      // Notify parent of conversation ID change
      if (onConversationIdChange) {
        onConversationIdChange(newConversationId)
      }

      // Save to history
      const firstUserMessage = updatedMessages.find(m => m.role === 'user')
      const title = firstUserMessage?.content.slice(0, 50) || 'New Conversation'
      updateConversation(newConversationId, updatedMessages, title)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get AI response'
      setError(errorMessage)
      console.error('Chat error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleNewChat = () => {
    setMessages([])
    setConversationId(undefined)
    setInput('')
    if (onConversationIdChange) {
      onConversationIdChange(undefined)
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
      {/* New Chat Button - Show when there are messages */}
      {messages.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<Icon icon='tabler:plus' />}
            onClick={handleNewChat}
            sx={{ fontSize: '0.75rem' }}
          >
            New Chat
          </Button>
        </Box>
      )}

      {/* Messages Container */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexGrow: 1,
              textAlign: 'center',
              color: 'text.secondary',
              pt: 2
            }}
          >
            <Icon icon='tabler:robot' fontSize={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Typography variant='h6' gutterBottom>
              Ask me anything about restaurant insights
            </Typography>
            <Typography variant='body2' sx={{ mb: 3 }}>
              Use the suggested questions below as a guide, or ask your own question
            </Typography>

            {/* Suggested Questions */}
            <Box sx={{ width: '100%', maxWidth: '100%' }}>
              <SuggestedQuestions
                onQuestionClick={question => {
                  setInput(question)
                  setTimeout(() => {
                    inputRef.current?.focus()
                  }, 100)
                }}
              />
            </Box>
          </Box>
        )}

        {messages.map((message, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start'
            }}
          >
            {message.role === 'assistant' && (
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 32,
                  height: 32
                }}
              >
                <Icon icon='tabler:robot' fontSize={18} />
              </Avatar>
            )}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                maxWidth: '75%',
                bgcolor: message.role === 'user' ? theme.palette.primary.main : theme.palette.action.hover,
                color: message.role === 'user' ? 'white' : 'text.primary',
                borderRadius: 2
              }}
            >
              <ChatMessageContent content={message.content} isUser={message.role === 'user'} />
              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  mt: 1,
                  opacity: 0.7,
                  fontSize: '0.7rem'
                }}
              >
                {message.timestamp.toLocaleTimeString()}
              </Typography>
            </Paper>

            {message.role === 'user' && (
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: 32,
                  height: 32
                }}
              >
                <Icon icon='tabler:user' fontSize={18} />
              </Avatar>
            )}
          </Box>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32
              }}
            >
              <Icon icon='tabler:robot' fontSize={18} />
            </Avatar>
            <Paper elevation={0} sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>
                  Thinking...
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Restaurant Selector */}
      <Box sx={{ mb: 2 }}>
        <RestaurantSelector
          value={selectedRestaurantIds}
          onChange={setSelectedRestaurantIds}
          useAllRestaurants={useAllRestaurants}
          onUseAllChange={setUseAllRestaurants}
          disabled={loading}
        />
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Input Area */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder='Ask about restaurant insights...'
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
        />
        <IconButton
          color='primary'
          onClick={handleSend}
          disabled={loading || !input.trim()}
          sx={{
            alignSelf: 'flex-end',
            mb: 0.5,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              bgcolor: theme.palette.primary.dark
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.action.disabledBackground
            }
          }}
        >
          {loading ? <CircularProgress size={20} color='inherit' /> : <Icon icon='tabler:send' />}
        </IconButton>
      </Box>
    </Box>
  )
}
